import { format, isToday, isTomorrow, isYesterday, parseISO } from 'date-fns';

export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'Unknown date';
  
  let date: Date;
  
  if (typeof dateString === 'string') {
    date = parseISO(dateString);
  } else if (dateString instanceof Date) {
    date = dateString;
  } else {
    return 'Invalid date';
  }
  
  if (isToday(date)) {
    return 'Today';
  }
  
  if (isTomorrow(date)) {
    return 'Tomorrow';
  }
  
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  
  return format(date, 'MMM d, yyyy');
}

export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'Unknown date';
  
  let date: Date;
  
  if (typeof dateString === 'string') {
    date = parseISO(dateString);
  } else if (dateString instanceof Date) {
    date = dateString;
  } else {
    return 'Invalid date';
  }
  
  return format(date, 'MMM d, yyyy h:mm a');
}

export function formatRelativeTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'Unknown time';
  
  let date: Date;
  
  if (typeof dateString === 'string') {
    date = parseISO(dateString);
  } else if (dateString instanceof Date) {
    date = dateString;
  } else {
    return 'Invalid date';
  }
  
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    if (diffInHours === 0) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
    }
    return `${diffInHours}h ago`;
  }
  
  if (diffInDays === 1) {
    return 'Yesterday';
  }
  
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }
  
  return format(date, 'MMM d');
}

// Convert hotel name to URL-friendly slug
export function hotelNameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
}

// Convert URL slug back to hotel name (for display purposes)
export function slugToHotelName(slug: string): string {
  return slug
    .replace(/-/g, ' ') // Replace hyphens with spaces
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'text-success-700 bg-success-50 border-success-200';
    case 'in-progress':
      return 'text-primary-700 bg-primary-50 border-primary-200';
    case 'blocked':
      return 'text-danger-700 bg-danger-50 border-danger-200';
    case 'not-started':
    default:
      return 'text-neutral-600 bg-neutral-50 border-neutral-200';
  }
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'high':
      return 'text-danger-700 bg-danger-50 border-danger-200';
    case 'medium':
      return 'text-warning-600 bg-warning-50 border-warning-200';
    case 'low':
    default:
      return 'text-neutral-600 bg-neutral-50 border-neutral-200';
  }
}

export function getCategoryColor(category: string): string {
  switch (category) {
    case 'setup':
      return 'text-primary-700 bg-primary-50 border-primary-200';
    case 'training':
      return 'text-accent-700 bg-accent-50 border-accent-200';
    case 'testing':
      return 'text-secondary-700 bg-secondary-50 border-secondary-200';
    case 'launch':
      return 'text-burgundy-700 bg-burgundy-50 border-burgundy-200';
    case 'support':
      return 'text-purple-700 bg-purple-50 border-purple-200';
    default:
      return 'text-neutral-600 bg-neutral-50 border-neutral-200';
  }
}
